import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import Hls from "hls.js"
import mpegts from "mpegts.js"

const PLAYLIST_ATTEMPTS = 12
const PLAYLIST_RETRY_MS = 2500
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "scroll"]
const IDLE_WARNING_SECONDS = 30

// Live camera streaming + recorded-file (VOD) playback for a JIMI device.
// The backend returns the stream URL right after accepting the turn-on
// command, without waiting for the device to actually confirm execution
// (see design.md decision #6) -- native <video> HLS support is used on
// Safari, hls.js everywhere else.
//
// A device can expose several camera channels (device.video_channels), each
// rendered as its own player and started/stopped independently: the device
// accepts simultaneous streams on different channels.
export default class extends Controller {
  static values = { imei: String, protocol: String, idleSeconds: { type: Number, default: 180 } }
  static targets = ["video", "liveStatus", "channel"]

  connect() {
    this.players = new Map()
    this.abandoned = new Set()
    this.live = new Set()
    this.liveSince = new Map()

    this.onPageHide = () => this.#stopAllLive("Transmissão parada.")
    this.onVisibilityChange = () => {
      if (document.visibilityState === "hidden") this.#stopAllLive("Transmissão pausada. Toque para retomar.")
    }
    this.onActivity = () => this.#resetIdleTimer()

    window.addEventListener("pagehide", this.onPageHide)
    document.addEventListener("visibilitychange", this.onVisibilityChange)
    ACTIVITY_EVENTS.forEach((name) => window.addEventListener(name, this.onActivity, { passive: true }))
  }

  disconnect() {
    window.removeEventListener("pagehide", this.onPageHide)
    document.removeEventListener("visibilitychange", this.onVisibilityChange)
    ACTIVITY_EVENTS.forEach((name) => window.removeEventListener(name, this.onActivity))
    this.#clearIdleTimers()
    clearInterval(this.clockTimer)

    this.live.forEach((channel) => this.#sendStop(channel))
    this.live.clear()
    this.channels().forEach((channel) => {
      this.abandoned.add(String(channel))
      this.#destroyHls(channel)
    })
  }

  fullscreen(event) {
    const video = this.#videoFor(this.#channelFrom(event))
    if (!video) return

    if (video.requestFullscreen) {
      video.requestFullscreen()
    } else if (video.webkitEnterFullscreen) {
      video.webkitEnterFullscreen()
    }
  }

  channels() {
    return this.channelTargets.map((element) => element.dataset.jimiStreamChannelParam)
  }

  async startLive(event) {
    const channel = this.#channelFrom(event)
    if (this.#channelElement(channel)?.dataset.state === "blocked") {
      this.#videoFor(channel)?.play().then(() => this.#setState(channel, "live")).catch(() => {})
      return
    }

    await this.#start(channel)
  }

  keepWatching() {
    this.#resetIdleTimer()
  }

  layout(event) {
    const grid = this.element.querySelector("[data-role='channel-grid']")
    if (!grid) return

    grid.dataset.layout = event.params.layout
    this.element.querySelectorAll("[data-jimi-stream-layout-param]").forEach((button) => {
      button.setAttribute("aria-pressed", String(button.dataset.jimiStreamLayoutParam === event.params.layout))
    })
  }

  async stopLive(event) {
    await this.#stop(this.#channelFrom(event))
  }

  // In parallel: each start waits for its own playlist to come up, and the
  // device serves channels independently -- queueing them would add every
  // channel's wait to the next one's.
  async startAll() {
    await Promise.all(this.channels().map((channel) => this.#start(channel)))
  }

  async stopAll() {
    await Promise.all(this.channels().map((channel) => this.#stop(channel)))
  }

  async playRecording(event) {
    const { file, from, to } = event.params
    const channel = String(event.params.channel || this.channels()[0])
    const status = this.#statusFor(channel)

    this.element.querySelector("[data-role='playback-placeholder']")?.classList.add("hidden")
    this.abandoned.delete(channel)
    if (status) status.textContent = status.dataset.loadingText || "Carregando gravação…"
    this.#setState(channel, "loading")

    const response = await post(`/devices/${this.imeiValue}/vod_playback`, {
      body: JSON.stringify({ protocol: this.protocolValue, channel, file, from, to }),
      contentType: "application/json",
      responseKind: "json"
    })

    if (!response.ok) {
      if (status) status.textContent = "Não foi possível carregar a gravação. Tente de novo."
      this.#setState(channel, "error")
      return
    }

    const { url } = await response.json
    await this.#play(channel, url)
  }

  async #start(channel) {
    const status = this.#statusFor(channel)
    status.textContent = status.dataset.loadingText || "Iniciando..."
    this.#setState(channel, "loading")

    const response = await post(`/devices/${this.imeiValue}/live_stream`, {
      body: JSON.stringify({ protocol: this.protocolValue, channel }),
      contentType: "application/json",
      responseKind: "json"
    })

    if (!response.ok) {
      status.textContent = "Não foi possível iniciar a transmissão. Toque para tentar de novo."
      this.#setState(channel, "error")
      return
    }

    this.abandoned.delete(String(channel))
    this.live.add(String(channel))
    this.#resetIdleTimer()

    const { url } = await response.json
    await this.#play(channel, url)
  }

  async #stop(channel, message = "Transmissão parada.") {
    this.abandoned.add(String(channel))
    this.live.delete(String(channel))
    this.liveSince.delete(String(channel))
    this.#setState(channel, "stopped")
    if (this.live.size === 0) this.#clearIdleTimers()

    await this.#sendStop(channel)

    this.#destroyHls(channel)
    this.#videoFor(channel)?.removeAttribute("src")
    const status = this.#statusFor(channel)
    if (status) status.textContent = message
  }

  #stopAllLive(message) {
    this.#clearIdleTimers()
    Array.from(this.live).forEach((channel) => this.#stop(channel, message))
  }

  // keepalive lets the request finish even when the page is being unloaded
  // (tab closed, Turbo navigation, phone locked) -- otherwise the device keeps
  // streaming over mobile data with nobody watching.
  #sendStop(channel) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    return fetch(`/devices/${this.imeiValue}/live_stream`, {
      method: "DELETE",
      keepalive: true,
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": token || "" },
      body: JSON.stringify({ protocol: this.protocolValue, channel })
    }).catch(() => {})
  }

  #resetIdleTimer() {
    this.#clearIdleTimers()
    if (this.live.size === 0) return

    this.idleTimer = setTimeout(
      () => this.#stopAllLive("Transmissão pausada para economizar dados. Toque para retomar."),
      this.idleSecondsValue * 1000
    )

    const warnAfter = this.idleSecondsValue - IDLE_WARNING_SECONDS
    if (warnAfter > 0) this.warnTimer = setTimeout(() => this.#showIdlePrompt(), warnAfter * 1000)
  }

  #clearIdleTimers() {
    clearTimeout(this.idleTimer)
    clearTimeout(this.warnTimer)
    clearInterval(this.countdownTimer)
    this.element.querySelector("[data-role='idle-prompt']")?.classList.add("hidden")
  }

  #showIdlePrompt() {
    const prompt = this.element.querySelector("[data-role='idle-prompt']")
    if (!prompt) return

    const countdown = prompt.querySelector("[data-role='idle-countdown']")
    let remaining = IDLE_WARNING_SECONDS
    if (countdown) countdown.textContent = remaining
    prompt.classList.remove("hidden")

    this.countdownTimer = setInterval(() => {
      remaining = Math.max(remaining - 1, 0)
      if (countdown) countdown.textContent = remaining
    }, 1000)
  }

  #channelElement(channel) {
    return this.channelTargets[this.#indexOf(channel)]
  }

  #setState(channel, state) {
    const element = this.#channelElement(channel)
    if (element) element.dataset.state = state

    if (state === "live" && !this.liveSince.has(String(channel))) this.liveSince.set(String(channel), Date.now())
    this.#tickClock()
  }

  #tickClock() {
    clearInterval(this.clockTimer)
    if (this.liveSince.size === 0) return

    const render = () => {
      this.liveSince.forEach((since, channel) => {
        const elapsed = this.#channelElement(channel)?.querySelector("[data-role='elapsed']")
        if (!elapsed) return

        const seconds = Math.floor((Date.now() - since) / 1000)
        elapsed.textContent = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`
      })
    }
    render()
    this.clockTimer = setInterval(render, 1000)
  }

  #channelFrom(event) {
    return String(event.params.channel)
  }

  #indexOf(channel) {
    return this.channels().indexOf(String(channel))
  }

  #videoFor(channel) {
    return this.videoTargets[this.#indexOf(channel)]
  }

  #statusFor(channel) {
    return this.liveStatusTargets[this.#indexOf(channel)]
  }

  // Live is HLS; recorded playback comes back as HTTP-FLV, which no browser
  // plays natively -- mpegts.js feeds it through Media Source Extensions.
  async #play(channel, url) {
    this.#destroyHls(channel)

    const video = this.#videoFor(channel)
    if (!video) return

    if (url.endsWith(".flv")) {
      this.#playFlv(channel, video, url)
      return
    }

    if (!(await this.#waitForPlaylist(channel, url))) return

    // hls.js first, native HLS only as fallback: Chrome answers "maybe" to
    // canPlayType("application/vnd.apple.mpegurl") without being able to
    // play HLS, so trusting it leaves a <video> with a src that never
    // starts. Native playback is really only Safari/iOS.
    if (Hls.isSupported()) {
      const hls = new Hls()
      this.players.set(String(channel), hls)
      hls.loadSource(url)
      hls.attachMedia(video)
      hls.on(Hls.Events.MANIFEST_PARSED, () => this.#startPlayback(channel, video))
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = url
      this.#startPlayback(channel, video)
    } else {
      this.#statusFor(channel).textContent = "Este navegador não reproduz o formato da transmissão."
      this.#setState(channel, "error")
      return
    }

    this.#statusFor(channel).textContent = "Ao vivo"
    this.#setState(channel, "live")
  }

  // play() rejects when the browser's autoplay policy blocks it. The promise
  // was being dropped, so the player just sat there with the controls
  // available and no hint of why.
  #startPlayback(channel, video) {
    video.play().catch(() => {
      if (this.abandoned.has(String(channel))) return
      this.#statusFor(channel).textContent = "Pronto. Use o play do player para iniciar."
      this.#setState(channel, "blocked")
    })
  }

  // The backend answers with the stream URL as soon as the hub accepts the
  // turn-on command, without waiting for the device to actually push (see
  // design.md decision #6) -- that takes a few seconds, and both hls.js and
  // native HLS give up on the first 404. A HEAD is no use here: the media
  // server answers 200 to any path, so the playlist itself has to be read.
  async #waitForPlaylist(channel, url) {
    const status = this.#statusFor(channel)

    for (let attempt = 1; attempt <= PLAYLIST_ATTEMPTS; attempt++) {
      if (this.abandoned.has(String(channel))) return false

      if (await this.#playlistReady(url)) return true

      status.textContent = `Aguardando o dispositivo… (${attempt}/${PLAYLIST_ATTEMPTS})`
      await new Promise((resolve) => setTimeout(resolve, PLAYLIST_RETRY_MS))
    }

    status.textContent = "O dispositivo não subiu a transmissão a tempo."
    this.#setState(channel, "error")
    return false
  }

  async #playlistReady(url) {
    try {
      const response = await fetch(url, { cache: "no-store" })
      if (!response.ok) return false

      return (await response.text()).trimStart().startsWith("#EXTM3U")
    } catch {
      // Stream not up yet: the media server refuses or resets the connection
      // until the device pushes. Expected during the wait, not an error.
      return false
    }
  }

  #playFlv(channel, video, url) {
    if (!mpegts.isSupported()) {
      this.#statusFor(channel).textContent = "Este navegador não reproduz o formato da gravação."
      return
    }

    const player = mpegts.createPlayer({ type: "flv", isLive: false, url })
    this.players.set(String(channel), player)
    player.attachMediaElement(video)
    player.load()
    this.#statusFor(channel).textContent = "Reproduzindo gravação"
    this.#setState(channel, "live")
    this.#startPlayback(channel, video)
  }

  #destroyHls(channel) {
    const player = this.players.get(String(channel))
    if (!player) return

    player.destroy()
    this.players.delete(String(channel))
  }
}
